import { IHttpService, IPromise, ILocationService } from "angular";
import { Ng1StateDeclaration } from "@uirouter/angularjs";
import { encode as base64encode } from "js-base64";
import Cookies from "js-cookie";
import { IUserSettings, IUser } from "./models";
import { UserProfileService } from "./userProfile/profileService";
import { ISnippet } from "../insights/models/models";
import { EnvironmentService } from "@gtmhub/env";
import { storage } from "@gtmhub/core/storage";
import { Language } from "@gtmhub/localization";
import { getCurrentUserId, getCurrentUserRoles } from "./current-user-storage";

export class CurrentUserService {
  public static $inject = ["$http", "EnvironmentService", "UserProfileService", "$location"];

  constructor(private $http: IHttpService, private env: EnvironmentService, private profileService: UserProfileService, private $location: ILocationService) {}

  private getUserSettingsFromStore(): IUserSettings {
    return storage.get("userSettings");
  }

  public demandsAreMet(toState: Ng1StateDeclaration): boolean {
    // we assume that states that require account information also need the userId information
    const stateRequiresAccount: boolean = toState && toState.data && toState.data.requiresAccount;
    const currentUserId = getCurrentUserId();
    const userLanguage = this.getUserLanguage(); // used by localisationService -> may be removed
    const roles = getCurrentUserRoles(); // used by analyticsService and timelineService -> may be removed

    return !stateRequiresAccount || (currentUserId && userLanguage && roles) ? true : false;
  }

  public verifyEmail(): IPromise<unknown> {
    const { email } = storage.get("profile");
    const url = this.env.getApiEndpoint("/users/send-verification-email/" + email);

    return this.$http.post(url, "");
  }

  public validateEmailVerification(emailVerificationCode: string): IPromise<unknown> {
    const id = getCurrentUserId();
    const url = this.env.getApiEndpoint(`/users/${id}/validate-verify-email`);
    const body = {
      emailVerificationCode,
    };

    return this.$http.post(url, body);
  }

  public getAnonymousId(): string {
    return storage.get("ajs_anonymous_id");
  }

  public clear(): void {
    storage.remove("userSettings");
    storage.remove("userId");
    storage.remove("language");
  }

  public setUserSettings(user: IUser): void {
    if (user.userSettings) {
      storage.set("userSettings", user.userSettings);
    } else {
      // this will be deleted when we migrate all users to have new userSettings property
      storage.set("userSettings", user.settings);
    }
  }

  public setMarketplaceRedirectCookie(): void {
    // Skip the first part of the subdomain
    // The cookie will be shared between subdomains
    const domain = this.$location.host().match(/[^.]*\.[^.]*$/);
    const lastVisitedUrlEncoded = base64encode(this.$location.absUrl().split("?")[0]);
    // Expires in 24h
    const expireDate = new Date();
    expireDate.setDate(expireDate.getDate() + 1);
    Cookies.set("marketplace_redirect", lastVisitedUrlEncoded, { domain: domain[0], expires: expireDate });
  }

  public removeMarketplaceRedirectCookie(): void {
    // Skip the first part of the subdomain
    // The cookie will be shared between subdomains
    const domain = this.$location.host().match(/[^.]*\.[^.]*$/);
    if (domain) {
      Cookies.remove("marketplace_redirect", { domain: domain[0] });
    }
  }

  public getUserLanguage(): Language {
    return storage.get<Language>("language");
  }

  public getUserSetting<T = any>(key: keyof IUserSettings): T {
    if (!key) {
      throw new Error("You must provide user setting key.");
    }

    const userSettings = this.getUserSettingsFromStore();
    return userSettings ? (userSettings[key] as T) : null;
  }

  public setUserSetting(userSetting: IUserSettings): IPromise<unknown> {
    const currentUserSettings = this.getUserSettingsFromStore();
    const userSettings: IUserSettings = {
      ...currentUserSettings,
      ...userSetting,
    };

    storage.set("userSettings", userSettings);

    if (userSetting.snippets) {
      userSetting.snippets = userSetting.snippets.map((snippet: ISnippet) => {
        const code = base64encode(snippet.code || "");
        return {
          ...snippet,
          code,
        };
      });
    }

    const url = this.env.getApiEndpoint("/users/settings");
    return this.$http.patch<unknown>(url, userSetting);
  }

  public setUserLanguage(language: string): void {
    storage.set("language", language);
  }

  public raiseProfileLanguageUpdated(language: string): void {
    const profile = this.profileService.getProfile();
    profile.language = language;
    this.profileService.userProfileUpdated(profile);
  }
}
